/**
 * IndexedDB 数据库服务
 * 用于科研项目智能评审系统的本地数据存储
 */

const DB_NAME = 'ScientificResearchDB'
const DB_VERSION = 1

// 存储对象名称
const STORES = {
  PROJECTS: 'projects',
  TIMELINE_DETAILS: 'timelineDetails'
}

/**
 * 打开数据库连接
 * @returns {Promise<IDBDatabase>}
 */
export function openDB() {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION)

    request.onerror = () => reject(request.error)
    request.onsuccess = () => resolve(request.result)

    request.onupgradeneeded = (event) => {
      const db = event.target.result

      // 创建项目存储对象
      if (!db.objectStoreNames.contains(STORES.PROJECTS)) {
        const projectStore = db.createObjectStore(STORES.PROJECTS, { keyPath: 'id' })
        projectStore.createIndex('currentStep', 'currentStep', { unique: false })
        projectStore.createIndex('status', 'status', { unique: false })
        projectStore.createIndex('code', 'code', { unique: false })
      }

      // 创建时间线详情存储对象
      if (!db.objectStoreNames.contains(STORES.TIMELINE_DETAILS)) {
        db.createObjectStore(STORES.TIMELINE_DETAILS, { keyPath: 'projectId' })
      }
    }
  })
}

/**
 * 检查数据库是否已初始化
 * @returns {Promise<boolean>}
 */
export async function isDBInitialized() {
  try {
    const db = await openDB()
    const transaction = db.transaction(STORES.PROJECTS, 'readonly')
    const store = transaction.objectStore(STORES.PROJECTS)
    const count = await new Promise((resolve, reject) => {
      const request = store.count()
      request.onsuccess = () => resolve(request.result)
      request.onerror = () => reject(request.error)
    })
    db.close()
    return count > 0
  } catch (error) {
    console.error('检查数据库初始化状态失败:', error)
    return false
  }
}

/**
 * 初始化数据库 - 导入所有项目数据
 * @param {Array} projects - 项目列表
 * @param {Object} timelineDetails - 时间线详情数据
 * @returns {Promise<void>}
 */
export async function initializeDB(projects, timelineDetails) {
  const db = await openDB()

  try {
    // 清空现有数据
    await clearAllData(db)

    // 导入项目数据
    await importProjects(db, projects)

    // 导入时间线详情数据
    await importTimelineDetails(db, timelineDetails)

    console.log('数据库初始化完成')
  } finally {
    db.close()
  }
}

/**
 * 清空所有数据
 * @param {IDBDatabase} db
 */
async function clearAllData(db) {
  const transaction = db.transaction([STORES.PROJECTS, STORES.TIMELINE_DETAILS], 'readwrite')

  await Promise.all([
    new Promise((resolve, reject) => {
      const request = transaction.objectStore(STORES.PROJECTS).clear()
      request.onsuccess = () => resolve()
      request.onerror = () => reject(request.error)
    }),
    new Promise((resolve, reject) => {
      const request = transaction.objectStore(STORES.TIMELINE_DETAILS).clear()
      request.onsuccess = () => resolve()
      request.onerror = () => reject(request.error)
    })
  ])
}

/**
 * 导入项目数据
 * @param {IDBDatabase} db
 * @param {Array} projects
 */
async function importProjects(db, projects) {
  const transaction = db.transaction(STORES.PROJECTS, 'readwrite')
  const store = transaction.objectStore(STORES.PROJECTS)

  const promises = projects.map(project => {
    return new Promise((resolve, reject) => {
      const request = store.add(project)
      request.onsuccess = () => resolve()
      request.onerror = () => reject(request.error)
    })
  })

  await Promise.all(promises)
}

/**
 * 导入时间线详情数据
 * @param {IDBDatabase} db
 * @param {Object} timelineDetails
 */
async function importTimelineDetails(db, timelineDetails) {
  const transaction = db.transaction(STORES.TIMELINE_DETAILS, 'readwrite')
  const store = transaction.objectStore(STORES.TIMELINE_DETAILS)

  const promises = Object.entries(timelineDetails).map(([projectId, data]) => {
    return new Promise((resolve, reject) => {
      const request = store.add({
        projectId: parseInt(projectId),
        ...data
      })
      request.onsuccess = () => resolve()
      request.onerror = () => reject(request.error)
    })
  })

  await Promise.all(promises)
}

/**
 * 获取所有项目
 * @returns {Promise<Array>}
 */
export async function getAllProjects() {
  const db = await openDB()

  try {
    const transaction = db.transaction(STORES.PROJECTS, 'readonly')
    const store = transaction.objectStore(STORES.PROJECTS)

    return await new Promise((resolve, reject) => {
      const request = store.getAll()
      request.onsuccess = () => resolve(request.result)
      request.onerror = () => reject(request.error)
    })
  } finally {
    db.close()
  }
}

/**
 * 根据 ID 获取项目
 * @param {number} id
 * @returns {Promise<Object|null>}
 */
export async function getProjectById(id) {
  const db = await openDB()

  try {
    const transaction = db.transaction(STORES.PROJECTS, 'readonly')
    const store = transaction.objectStore(STORES.PROJECTS)

    return await new Promise((resolve, reject) => {
      const request = store.get(id)
      request.onsuccess = () => resolve(request.result || null)
      request.onerror = () => reject(request.error)
    })
  } finally {
    db.close()
  }
}

/**
 * 根据流程步骤获取项目
 * @param {number} currentStep
 * @returns {Promise<Array>}
 */
export async function getProjectsByStep(currentStep) {
  const db = await openDB()

  try {
    const transaction = db.transaction(STORES.PROJECTS, 'readonly')
    const store = transaction.objectStore(STORES.PROJECTS)
    const index = store.index('currentStep')

    return await new Promise((resolve, reject) => {
      const request = index.getAll(currentStep)
      request.onsuccess = () => resolve(request.result)
      request.onerror = () => reject(request.error)
    })
  } finally {
    db.close()
  }
}

/**
 * 获取项目时间线详情
 * @param {number} projectId
 * @returns {Promise<Object|null>}
 */
export async function getProjectTimeline(projectId) {
  const db = await openDB()

  try {
    const transaction = db.transaction(STORES.TIMELINE_DETAILS, 'readonly')
    const store = transaction.objectStore(STORES.TIMELINE_DETAILS)

    return await new Promise((resolve, reject) => {
      const request = store.get(projectId)
      request.onsuccess = () => resolve(request.result || null)
      request.onerror = () => reject(request.error)
    })
  } finally {
    db.close()
  }
}

/**
 * 添加或更新项目
 * @param {Object} project
 * @returns {Promise<void>}
 */
export async function saveProject(project) {
  const db = await openDB()

  try {
    const transaction = db.transaction(STORES.PROJECTS, 'readwrite')
    const store = transaction.objectStore(STORES.PROJECTS)

    return await new Promise((resolve, reject) => {
      const request = store.put(project)
      request.onsuccess = () => resolve()
      request.onerror = () => reject(request.error)
    })
  } finally {
    db.close()
  }
}

/**
 * 删除项目
 * @param {number} id
 * @returns {Promise<void>}
 */
export async function deleteProject(id) {
  const db = await openDB()

  try {
    const transaction = db.transaction(STORES.PROJECTS, 'readwrite')
    const store = transaction.objectStore(STORES.PROJECTS)

    return await new Promise((resolve, reject) => {
      const request = store.delete(id)
      request.onsuccess = () => resolve()
      request.onerror = () => reject(request.error)
    })
  } finally {
    db.close()
  }
}

/**
 * 搜索项目
 * @param {string} keyword
 * @returns {Promise<Array>}
 */
export async function searchProjects(keyword) {
  const allProjects = await getAllProjects()
  const lowerKeyword = keyword.toLowerCase()

  return allProjects.filter(project =>
    project.name.toLowerCase().includes(lowerKeyword) ||
    project.code.toLowerCase().includes(lowerKeyword) ||
    project.leader.toLowerCase().includes(lowerKeyword)
  )
}

export { STORES }
export default {
  openDB,
  isDBInitialized,
  initializeDB,
  getAllProjects,
  getProjectById,
  getProjectsByStep,
  getProjectTimeline,
  saveProject,
  deleteProject,
  searchProjects
}
